// Pure geospatial math helpers — no dependencies.

const R_KM = 6371; // mean Earth radius in km

const toRad = (deg) => (deg * Math.PI) / 180;
const toDeg = (rad) => (rad * 180) / Math.PI;

// Great-circle distance between two points in kilometres.
export function haversineKm(lat1, lon1, lat2, lon2) {
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R_KM * c;
}

// Initial bearing (forward azimuth) from point 1 to point 2, in degrees 0..360.
export function bearing(lat1, lon1, lat2, lon2) {
  const φ1 = toRad(lat1);
  const φ2 = toRad(lat2);
  const Δλ = toRad(lon2 - lon1);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x =
    Math.cos(φ1) * Math.sin(φ2) -
    Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

// Sample points along the great circle from point 1 to point 2 (spherical
// interpolation / slerp). Returns `steps + 1` points as [lat, lon] pairs,
// inclusive of both endpoints. Handles antimeridian crossings naturally because
// it interpolates on the unit sphere, not in lon/lat space.
//
// Used to draw the route polyline on the 2D map and to build the 3D camera path.
export function interpolateGreatCircle(lat1, lon1, lat2, lon2, steps = 128) {
  const φ1 = toRad(lat1);
  const λ1 = toRad(lon1);
  const φ2 = toRad(lat2);
  const λ2 = toRad(lon2);

  // Angular distance between the two points (same as haversine, in radians).
  const dφ = φ2 - φ1;
  const dλ = λ2 - λ1;
  const a =
    Math.sin(dφ / 2) ** 2 +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(dλ / 2) ** 2;
  const δ = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  const n = Math.max(1, Math.floor(steps));
  const points = [];

  // Degenerate case: coincident points — nothing to interpolate.
  if (δ === 0) {
    for (let i = 0; i <= n; i++) points.push([lat1, lon1]);
    return points;
  }

  const sinδ = Math.sin(δ);
  const x1 = Math.cos(φ1) * Math.cos(λ1);
  const y1 = Math.cos(φ1) * Math.sin(λ1);
  const z1 = Math.sin(φ1);
  const x2 = Math.cos(φ2) * Math.cos(λ2);
  const y2 = Math.cos(φ2) * Math.sin(λ2);
  const z2 = Math.sin(φ2);

  for (let i = 0; i <= n; i++) {
    const f = i / n;
    const A = Math.sin((1 - f) * δ) / sinδ;
    const B = Math.sin(f * δ) / sinδ;
    const x = A * x1 + B * x2;
    const y = A * y1 + B * y2;
    const z = A * z1 + B * z2;
    const φ = Math.atan2(z, Math.sqrt(x * x + y * y));
    const λ = Math.atan2(y, x);
    points.push([toDeg(φ), toDeg(λ)]);
  }
  return points;
}
